import * as React from "react";
import clsx from "clsx";
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";
import { UseFormGetValues } from "react-hook-form";
import _ from "lodash";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";

interface Props {
  name: string;
  label: string;
  helpText?: string;
  defaultValue?: string | number | string[] | undefined;
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
}

export const DurationInputFormField: React.FC<Props> = (props) => {
  const errors = props.errors;
  const hasError = !!errors?.[props.name];
  const errorMessage = errors?.[props.name]?.message as string;
  const fieldName = props.name;
  const setValue = props.setValue;

  const [years, setYears] = React.useState(0);
  const [months, setMonths] = React.useState(0);
  const [days, setDays] = React.useState(0);
  const [hours, setHours] = React.useState(0);
  const [minutes, setMinutes] = React.useState(0);
  const [seconds, setSeconds] = React.useState(0);

  let { helpText, name } = props;
  if (!helpText && name === "name") {
    helpText = "Visible only for admins";
  }

  if (!helpText && name === "title") {
    helpText = "Visible for users in the app";
  }

  const value: string | undefined = props.watch(
    props.name as any,
    props.getValues()[props.name]
  );

  React.useEffect(() => {
    if (!value) {
      return;
    }

    if (!value.startsWith("P")) {
      return;
    }

    let processed = value.substring(1);
    let dayTimeParts = processed.split("T");

    if (dayTimeParts[0]) {
      let dayParts = dayTimeParts[0].split(/([A-Z])/).filter((x) => x !== "");

      const hasValidPairs = dayParts.length % 2 !== 0;
      if (hasValidPairs) {
        return;
      }

      for (let i = 0; i < dayParts.length; i = i + 2) {
        let value = parseInt(dayParts[i], 10);
        const type = dayParts[i + 1];

        if (_.isNaN(value)) {
          value = 0;
        }

        switch (type) {
          case "Y":
            setYears(value);
            break;
          case "M":
            setMonths(value);
            break;
          case "D":
            setDays(value);
            break;
        }
      }
    }

    if (dayTimeParts[1]) {
      let timeParts = dayTimeParts[1].split(/([A-Z])/).filter((x) => x !== "");

      const hasValidPairs = timeParts.length % 2 !== 0;
      if (hasValidPairs) {
        return;
      }

      for (let i = 0; i < timeParts.length; i = i + 2) {
        let value = parseInt(timeParts[i], 10);
        if (_.isNaN(value)) {
          value = 0;
        }
        const type = timeParts[i + 1];

        switch (type) {
          case "H":
            setHours(value);
            break;
          case "I":
            setMinutes(value);
            break;
          case "S":
            setSeconds(value);
            break;
        }
      }
    }
  }, [value]);

  const updateValue = (
    y?: number,
    m?: number,
    d?: number,
    h?: number,
    i?: number,
    s?: number
  ) => {
    let newYears = years;
    let newMonths = months;
    let newDays = days;
    let newHours = hours;
    let newMinutes = minutes;
    let newSeconds = seconds;

    if (!_.isNil(y)) {
      newYears = y;
      setYears(y);
    }
    if (!_.isNil(m)) {
      newMonths = m;
      setMonths(m);
    }
    if (!_.isNil(d)) {
      newDays = d;
      setDays(d);
    }

    if (!_.isNil(h)) {
      newHours = h;
      setHours(h);
    }
    if (!_.isNil(i)) {
      newMinutes = i;
      setMinutes(i);
    }
    if (!_.isNil(s)) {
      newSeconds = s;
      setSeconds(s);
    }

    const strVal = `P${newYears || 0}Y${newMonths || 0}M${newDays || 0}DT${
      newHours || 0
    }H${newMinutes || 0}M${newSeconds || 0}S`;
    setValue(fieldName as any, strVal);
  };

  return (
    <div className={clsx({ "form-group": true, "has-error": hasError })}>
      <input
        className="form-control"
        type="hidden"
        {...props.register(props.name as any)}
        defaultValue={props.defaultValue}
      />

      <div className="row">
        <div className="col">
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            Years
          </StyledText>
          <input
            type="number"
            className="w-100 form-control"
            value={years}
            size={3}
            min={0}
            onChange={(e) => {
              updateValue(parseInt(e.target.value));
            }}
          />
        </div>
        <div className="col">
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            Months
          </StyledText>
          <input
            type="number"
            className="w-100 form-control"
            value={months}
            size={3}
            min={0}
            onChange={(e) => {
              updateValue(undefined, parseInt(e.target.value));
            }}
          />
        </div>
        <div className="col">
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            Days
          </StyledText>
          <input
            type="number"
            className="w-100 form-control"
            value={days}
            size={3}
            min={0}
            onChange={(e) => {
              updateValue(undefined, undefined, parseInt(e.target.value));
            }}
          />
        </div>
        <div className="col">
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            Hours
          </StyledText>
          <input
            type="number"
            className="w-100 form-control"
            value={hours}
            size={3}
            min={0}
            onChange={(e) => {
              updateValue(
                undefined,
                undefined,
                undefined,
                parseInt(e.target.value)
              );
            }}
          />
        </div>
        <div className="col">
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            Minutes
          </StyledText>
          <input
            type="number"
            className="w-100 form-control"
            value={minutes}
            size={3}
            min={0}
            onChange={(e) => {
              updateValue(
                undefined,
                undefined,
                undefined,
                undefined,
                parseInt(e.target.value)
              );
            }}
          />
        </div>
        <div className="col">
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            Seconds
          </StyledText>
          <input
            type="number"
            className="w-100 form-control"
            value={seconds}
            size={3}
            min={0}
            onChange={(e) => {
              updateValue(
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                parseInt(e.target.value)
              );
            }}
          />
        </div>
      </div>

      {hasError && (
        <StyledText
          color={colors.errorDark}
          className="bodySubtextRegular mt-2"
        >
          {errorMessage}
        </StyledText>
      )}
      {helpText && (
        <StyledText
          color={colors.textSecondary}
          className="bodySubtextRegular mt-2"
        >
          {helpText}
        </StyledText>
      )}
    </div>
  );
};
